import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarDays, CalendarRange, Cog, Plus, Wallet } from 'lucide-react'
import { getExpenses } from '../../api/operatingCosts'
import Button from '../../components/Button'
import { Card, Tile } from '../../components/Summary'
import { companyNow, fmtDay } from '../../utils/format'
import { money } from '../sales/salesShared'
import ExpenseTabs from './ExpenseTabs'
import { isSpending, periodRange, total } from './expensesShared'

// The Expenses summary: what the business spent this month and year, on
// what, and the costs the system records by itself.
export default function OperatingMenu() {
  const [rows, setRows] = useState(null)

  useEffect(() => {
    let cancelled = false
    // From whichever is earlier, the start of the year or of last month.
    const [lastMonthStart] = periodRange('last-month')
    const [yearStart, today] = periodRange('year')
    getExpenses({ start: lastMonthStart < yearStart ? lastMonthStart : yearStart, end: today })
      .then((data) => { if (!cancelled) setRows(Array.isArray(data) ? data : []) })
      .catch(() => { if (!cancelled) setRows([]) })
    return () => { cancelled = true }
  }, [])

  return (
    <div className="section-page">
      <ExpenseTabs />
      <header className="section-header">
        <h1>Expenses</h1>
        <div className="section-actions">
          <Button as={Link} to="/operating/new" variant="primary" size="sm"><Plus size={14} />Record an expense</Button>
        </div>
      </header>
      {rows ? <Summary rows={rows} /> : <p className="sum-state">Loading expenses…</p>}
    </div>
  )
}

function Summary({ rows }) {
  const now = companyNow()
  const month = now.toLocaleDateString(undefined, { month: 'long' })
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toLocaleDateString(undefined, { month: 'long' })
  const inRange = (id) => { const [a, b] = periodRange(id); return rows.filter((r) => r.date >= a && r.date <= b) }
  const thisMonth = inRange('month')
  const spentMonth = total(thisMonth.filter(isSpending))
  const spentLast = total(inRange('last-month').filter(isSpending))
  const spentYear = total(inRange('year').filter(isSpending))
  const autoMonth = thisMonth.filter((r) => !isSpending(r))
  const change = spentLast > 0 ? ((spentMonth - spentLast) / spentLast) * 100 : null

  const byCategory = (list) => Object.entries(list.reduce((m, r) => ({ ...m, [r.category]: (m[r.category] || 0) + Number(r.amount || 0) }), {}))
    .sort((a, b) => b[1] - a[1])
  const categories = byCategory(thisMonth.filter(isSpending))
  const autoCategories = byCategory(autoMonth)
  const biggest = categories[0]?.[1] || 0
  const recent = rows.filter(isSpending).slice(0, 6)

  return (
    <>
      <div className="sum-tiles">
        <Tile label={`Spent in ${month}`} href="/operating/expenses" icon={Wallet} value={money(spentMonth)}
          note={change == null ? (spentLast > 0 ? '' : 'Nothing last month to compare') : `${change >= 0 ? 'Up' : 'Down'} ${Math.abs(change).toFixed(0)}% on ${lastMonth}`}
          trend={change == null ? undefined : (change > 0 ? 'down' : 'up')} />
        <Tile label={`Spent in ${lastMonth}`} href="/operating/expenses?period=last-month" icon={CalendarDays} value={money(spentLast)} />
        <Tile label="Spent this year" href="/operating/expenses?period=year" icon={CalendarRange} value={money(spentYear)} />
        <Tile label={`Automatic costs in ${month}`} href="/operating/expenses?show=auto" icon={Cog} value={money(total(autoMonth))}
          note={autoCategories.length ? autoCategories.map(([c]) => c).slice(0, 2).join(', ') : 'None this month'} />
      </div>

      <div className="sum-columns">
        <div className="sum-main">
          <Card title={`${month} by account`} action={<Link to="/operating/expenses">All expenses</Link>}>
            {categories.length ? (
              <ul className="sum-list exp-bars">
                {categories.map(([cat, amt]) => (
                  <li key={cat}>
                    <span className="sum-list-main">
                      {cat}
                      <span className="exp-bar" aria-hidden="true"><span style={{ width: `${Math.max(3, (amt / biggest) * 100)}%` }} /></span>
                    </span>
                    <span className="sum-list-side">{money(amt)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="sum-muted">Nothing spent yet this month. <Link to="/operating/new">Record an expense</Link></p>
            )}
          </Card>

          <Card title="Recent expenses" action={<Link to="/operating/expenses">See all</Link>}>
            {recent.length ? (
              <ul className="sum-list">
                {recent.map((r) => (
                  <li key={`${r.je_id}-${r.account_code}`}>
                    <span className="sum-list-main">
                      {r.what || r.category}
                      <span className="sum-muted"> · {r.category}{r.vendor_name ? ` · ${r.vendor_name}` : ''} · {fmtDay(r.date, { year: false })}</span>
                    </span>
                    <span className="sum-list-side">{money(r.amount)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="sum-muted">No expenses recorded yet.</p>
            )}
          </Card>
        </div>

        <div className="sum-side">
          <Card title="Recorded automatically" action={<Link to="/operating/expenses?show=auto">See them</Link>}>
            <p className="sum-muted exp-note">Costs the system records for you in {month}: they aren’t entered here.</p>
            {autoCategories.length ? (
              <ul className="sum-list">
                {autoCategories.map(([cat, amt]) => (
                  <li key={cat}><span className="sum-list-main">{cat}</span><span className="sum-list-side">{money(amt)}</span></li>
                ))}
              </ul>
            ) : (
              <p className="sum-muted">None this month.</p>
            )}
          </Card>
        </div>
      </div>
    </>
  )
}
